import React, { useState, useEffect, useMemo } from 'react';
import FormatSelector from '../components/FormatSelector';
import InputPane from '../components/InputPane';
import DiffView from '../components/DiffView';
import { parseData } from '../utils/parsers';
import { generateDiff } from '../utils/diffEngine';
import { getSampleData } from '../utils/sampleData';

export default function ToolPage({ tool }) {
  const sampleData = getSampleData(tool.format);
  const [originalText, setOriginalText] = useState(sampleData.original);
  const [modifiedText, setModifiedText] = useState(sampleData.modified);
  const [csvPrimaryKey, setCsvPrimaryKey] = useState('');

  const [originalParsed, setOriginalParsed] = useState(null);
  const [modifiedParsed, setModifiedParsed] = useState(null);
  const [originalError, setOriginalError] = useState(null);
  const [modifiedError, setModifiedError] = useState(null);
  const [detectedFormat, setDetectedFormat] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);

  // Parse original text
  useEffect(() => {
    if (!originalText.trim()) {
      setOriginalParsed(null);
      setOriginalError(null);
      return;
    }

    const result = parseData(originalText, tool.format, { csvPrimaryKey });

    if (result.success) {
      setOriginalParsed(result.data);
      setOriginalError(null);
      setDetectedFormat(result.format);
      if (result.headers) {
        setCsvHeaders(result.headers);
      }
    } else {
      setOriginalParsed(null);
      setOriginalError(result.error);
    }
  }, [originalText, tool.format, csvPrimaryKey]);

  // Parse modified text
  useEffect(() => {
    if (!modifiedText.trim()) {
      setModifiedParsed(null);
      setModifiedError(null);
      return;
    }

    const result = parseData(modifiedText, tool.format, { csvPrimaryKey });

    if (result.success) {
      setModifiedParsed(result.data);
      setModifiedError(null);
      if (result.headers && csvHeaders.length === 0) {
        setCsvHeaders(result.headers);
      }
    } else {
      setModifiedParsed(null);
      setModifiedError(result.error);
    }
  }, [modifiedText, tool.format, csvPrimaryKey]);

  // Generate diff
  const diffResult = useMemo(() => {
    if (!originalParsed || !modifiedParsed) {
      return null;
    }
    return generateDiff(originalParsed, modifiedParsed);
  }, [originalParsed, modifiedParsed]);

  const handleClear = () => {
    setOriginalText('');
    setModifiedText('');
    setCsvPrimaryKey('');
  };

  return (
    <div className="tool-page">
      <header className="tool-header">
        <div className="tool-header-content">
          <a href="#/" className="back-link">← All Tools</a>
          <h1 className="tool-h1">{tool.keyword}</h1>
          <p className="tool-intro">
            Compare and analyze {tool.format.toUpperCase()} files instantly with our free online tool.
            Identify structural differences, track changes, and validate your {tool.format.toUpperCase()} data with precision.
          </p>
        </div>
      </header>

      <section className="tool-diff-section">
        <div className="app-controls">
          <FormatSelector
            format={tool.format}
            onFormatChange={() => {}}
            detectedFormat={detectedFormat}
            csvHeaders={csvHeaders}
            csvPrimaryKey={csvPrimaryKey}
            onCsvPrimaryKeyChange={setCsvPrimaryKey}
          />

          <div className="action-buttons">
            <button className="action-btn" onClick={handleClear}>
              🗑 Clear
            </button>
          </div>
        </div>

        <div className="app-content">
          <div className="input-section">
            <InputPane
              label={`Original ${tool.format.toUpperCase()}`}
              value={originalText}
              onChange={setOriginalText}
              placeholder={`Paste your first ${tool.format.toUpperCase()} content here...`}
              error={originalError}
            />
            <InputPane
              label={`Modified ${tool.format.toUpperCase()}`}
              value={modifiedText}
              onChange={setModifiedText}
              placeholder={`Paste your second ${tool.format.toUpperCase()} content here...`}
              error={modifiedError}
            />
          </div>

          <div className="diff-section">
            <DiffView
              diffResult={diffResult}
              original={originalParsed}
              modified={modifiedParsed}
            />
          </div>
        </div>
      </section>

      <article className="tool-content">
        <h2 className="tool-h2">Why Use {tool.keyword}?</h2>
        <div className="tool-seo-content">
          {tool.seoBlurb.split('\n\n').map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>
      </article>

      <aside className="tool-features">
        <h3 className="tool-h3">Features</h3>
        <ul className="features-list">
          <li>✓ Side-by-side comparison view</li>
          <li>✓ Semantic diff view with structural analysis</li>
          <li>✓ Tree view for exploring nested structures</li>
          <li>✓ Raw diff view for line-by-line changes</li>
          <li>✓ Syntax highlighting and formatting</li>
          <li>✓ No installation or registration required</li>
          <li>✓ 100% client-side processing - your data stays private</li>
          <li>✓ Works on desktop, tablet, and mobile devices</li>
        </ul>
      </aside>

      <style>{`
        .tool-page {
          width: 100%;
          padding-top: 1rem;
        }

        .tool-header {
          margin-bottom: 2rem;
          padding: 0 1rem;
        }

        .tool-header-content {
          max-width: 900px;
          margin: 0 auto;
        }

        .back-link {
          display: inline-block;
          color: var(--accent-color);
          text-decoration: none;
          margin-bottom: 1rem;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .back-link:hover {
          text-decoration: underline;
        }

        .tool-h1 {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 1rem;
        }

        .tool-intro {
          font-size: 1.125rem;
          color: var(--text-secondary);
          line-height: 1.6;
        }

        .tool-diff-section {
          margin-bottom: 3rem;
        }

        .tool-content {
          max-width: 900px;
          margin: 0 auto 2rem;
          padding: 2rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
        }

        .tool-h2 {
          font-size: 1.75rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 1rem;
        }

        .tool-seo-content p {
          color: var(--text-secondary);
          line-height: 1.8;
          margin-bottom: 1rem;
        }

        .tool-features {
          max-width: 900px;
          margin: 0 auto;
          padding: 1.5rem 2rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
        }

        .tool-h3 {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 1rem;
        }

        .features-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 0.75rem;
        }

        .features-list li {
          color: var(--text-secondary);
          font-size: 0.9375rem;
          line-height: 1.5;
        }

        @media (max-width: 768px) {
          .tool-h1 {
            font-size: 1.75rem;
          }

          .tool-content,
          .tool-features {
            padding: 1.5rem;
          }

          .features-list {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
